import { type FunctionComponentWithRef, useCallback } from "react";
import { graphql, useFragment } from "react-relay";

import { withHighlightedHeadline } from "scmp-app/components/content/content-item-render/highlight/hoc";
import { useContentItemProviders } from "scmp-app/components/content/content-item-render/hooks";
import type { ContentItemRenderVariantProps } from "scmp-app/components/content/content-item-render/types";
import { getContentSponsor } from "scmp-app/components/content/content-sponsor-tag/helpers";
import type { homeNativeArticleContentItemContent$key } from "scmp-app/queries/__generated__/homeNativeArticleContentItemContent.graphql";

import {
  Container,
  CoverImage,
  Headline,
  SectionLabel,
  SponsorBox,
  SponsorName,
  SponsorType,
  StyledEntityLink,
  TextGroup,
} from "./styles";

export type Props = ContentItemRenderVariantProps & {
  reference: homeNativeArticleContentItemContent$key;
};

const Component: FunctionComponentWithRef<Props, HTMLDivElement> = ({
  className,
  onClick,
  ref,
  reference,
}) => {
  const content = useFragment(
    graphql`
      fragment homeNativeArticleContentItemContent on Content {
        entityId
        urlAlias
        ...entityLink
        ...helpersUseContentSponsorContent
        ...hooksContentItemProvidersContent
          @arguments(
            withSections: true
            withHeadline: true
            withCoverImage: true
            withCoverImageSize540x360: true
          )
      }
    `,
    reference,
  );

  const providers = useContentItemProviders(content);
  const sponsor = getContentSponsor(content);

  const handleClick = useCallback(() => {
    onClick?.(content.entityId, content.urlAlias);
  }, [content.entityId, content.urlAlias, onClick]);

  return (
    <Container className={className} ref={ref}>
      <StyledEntityLink onClick={handleClick} reference={content}>
        <TextGroup>
          <SectionLabel>{providers.sections({ isDisableLink: true })}</SectionLabel>
          <Headline>
            {providers.headline({
              preferSocialHeadline: true,
              skipHeadlineSponsorStyle: true,
            })}
          </Headline>
          {sponsor?.name && (
            <SponsorBox>
              <SponsorType>{sponsor.type}</SponsorType>
              <SponsorName>{sponsor.name}</SponsorName>
            </SponsorBox>
          )}
        </TextGroup>
        <CoverImage>
          {providers.coverImage({
            responsiveVariants: {
              desktopUp: "size540x360",
              mobileUp: "size540x360",
              tabletUp: "size540x360",
            },
            withCaption: false,
          })}
        </CoverImage>
      </StyledEntityLink>
    </Container>
  );
};

Component.displayName = "ContentItemHomeNativeArticle";

export const ContentItemHomeNativeArticle = withHighlightedHeadline(Component);
